import styled, { DefaultTheme } from "styled-components";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { getCornerRadius } from "@swiggy-private/dls-web-common/dist/components/themes/CornerRadiusMapping/mapping";

const ICON_WIDTH = "20px";
const ICON_HEIGHT = "20px";
const BOTTOM_TEXT_CONTAINER_BG =
    "linear-gradient(90deg, rgba(255, 255, 255, 0.80) 0%, rgba(240, 240, 245, 0.80) 20%, rgba(255, 255, 255, 0.80) 100%)";

export const SAVINGS_LINE_ITEM_CONSTANTS = {
    TOOLTIP_CONTAINER_TOP_OFFSET: "42px",
    TOOLTIP_CONTAINER_LEFT_OFFSET: "-2px",
};

interface IStyles {
    theme?: DefaultTheme;
    hasNoSubtitle?: boolean;
    isSecondaryButton?: boolean;
    color?: string;
}

export const OuterContainer = styled.div(({ theme }) => {
    return {
        position: "relative",
        padding: theme.spacing.medium,
    };
});

export const Container = styled.div<IStyles>(({ theme, hasNoSubtitle }) => {
    return {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        position: "relative",

        // Button reset styles when rendered as button (for Apply Coupon container)
        "&[type='button']": {
            background: "transparent",
            width: "100%",
            border: "none",
            padding: 0,
            margin: 0,
            cursor: "pointer",
            textAlign: "inherit" as const,
            fontFamily: "inherit",
        },

        "::before": {
            content: hasNoSubtitle ? "''" : undefined,
            position: "absolute",
            top: `calc((${theme.spacing.xxSmall} + ${theme.spacing.xxxSmall}) * -1)`,
            right: `calc(${theme.spacing.medium} * -1)`,
            left: "0",
            bottom: `calc(${theme.spacing.xxSmall} * -1)`,
        },
    };
});

export const TooltipContainer = styled.div({
    position: "absolute",
    left: SAVINGS_LINE_ITEM_CONSTANTS.TOOLTIP_CONTAINER_LEFT_OFFSET,
    top: SAVINGS_LINE_ITEM_CONSTANTS.TOOLTIP_CONTAINER_TOP_OFFSET,
    zIndex: 2,
});

export const TextWrapper = styled(Text)(({ theme }) => {
    return {
        marginLeft: theme.spacing.small,
        marginRight: theme.spacing.xSmall,
    };
});

export const TooltipNodeWrapper = styled.div(({ theme }) => {
    return {
        marginLeft: theme.spacing.small,
        marginRight: theme.spacing.xSmall,
    };
});

export const ImageContainer = styled.div<{ isBlackUser: boolean }>(({ isBlackUser }) => {
    const flexCenter = isBlackUser ? { display: "flex", justifyContent: "center", alignItems: "center" } : {};
    return {
        width: ICON_WIDTH,
        height: ICON_HEIGHT,
        alignSelf: "flex-start",
        ...flexCenter,
    };
});

export const TitleContainer = styled.div(() => {
    return {
        display: "flex",
        alignItems: "center",
    };
});

export const SubtitleContainer = styled.div(({ theme }) => {
    return {
        marginLeft: `calc(${theme.spacing.small} + ${ICON_WIDTH})`,
        display: "flex",
        alignItems: "center",
        position: "relative",
        width: "fit-content",

        "::before": {
            content: "''",
            position: "absolute",
            top: `calc(${theme.spacing.xxSmall} * -1)`,
            right: `calc(${theme.spacing.xxxSmall} * -1)`,
            left: `calc(${theme.spacing.xxxSmall} * -1)`,
            bottom: `calc(${theme.spacing.xxSmall} * -1)`,
        },
    };
});

export const OneTapButton = styled.div<IStyles>(({ theme, isSecondaryButton, color }) => {
    const primaryColor = color || theme.colors[theme.colorMode].primary;

    const _color = isSecondaryButton ? theme.colors[theme.colorMode].text_Med_Emphasis : primaryColor;

    return {
        minWidth: "60px",
        border: `1px solid ${_color}`,
        padding: theme.spacing.xSmall,
        borderRadius: getCornerRadius(theme, "small"),
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "34px",
        overflow: "hidden",
    };
});

export const LabelContainer = styled.div(() => {
    return {
        alignSelf: "flex-start",

        // Reset button styles when rendered as button
        background: "none",
        border: "none",
        padding: 0,
        margin: 0,
        cursor: "pointer",
        textAlign: "inherit" as const,
        fontFamily: "inherit",
    };
});

export const IconWrapper = styled.div(() => {
    return {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",

        span: {
            marginTop: `-1px`,
        },
    };
});

export const BottomTextContainer = styled.div(({ theme }) => {
    return {
        padding: `${theme.spacing.xxSmall} 0 ${theme.spacing.xxSmall} calc(${ICON_WIDTH} + ${theme.spacing.small})`,
        marginTop: theme.spacing.xxSmall,
        background: BOTTOM_TEXT_CONTAINER_BG,
    };
});

export const RightChevronWrapper = styled.div(() => {
    return {
        marginRight: "-3px",
        marginTop: "1px",
    };
});

export const SubtitleIconWrapper = styled.div(({ theme }) => {
    return {
        display: "inline",
        marginLeft: theme.spacing.xxxSmall,
    };
});

export const ButtonTextWrapper = styled(Text)<{ textColor?: string; isSecondaryButton?: boolean }>(({
    textColor,
    isSecondaryButton,
}) => {
    return {
        marginTop: "1px",
        ...(!isSecondaryButton &&
            textColor && {
                color: textColor,
            }),
    };
});

// display inline to keep arrow icon inline with 2 liner text
export const Subtitle = styled(Text)(() => {
    return {
        display: "inline",
        div: {
            display: "inline",
        },
    };
});

export const GenericIconWrapper = styled(GenericIcon)(({ theme }) => {
    return {
        display: "inline-flex!important",
        transform: `translatey(${theme.spacing.xxxSmall})`,
    };
});
